import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// 1. Ant Design
import { Button, Descriptions, Drawer, Skeleton, Table, Tag, Typography } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
// 2. External libraries
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
// 5. Types, utils, styles
import { INVOICE_STATUS_COLORS, INVOICE_VOID_LABEL, PAYMENT_STATE_COLORS, PAYMENT_STATE_LABELS } from "@/types/company/settlements.common";
import { excelSheet, exportExcel } from "@/utils/excel";
import { formatDate, formatMoney } from "./format";
import styles from "@/styles/Company/Settlements.module.less";
const { Text } = Typography;
const invoiceStatusTag = (invoice) => invoice.status === "VOID" ? (_jsx(Tag, { color: INVOICE_STATUS_COLORS.VOID, children: INVOICE_VOID_LABEL })) : invoice.paymentState ? (_jsx(Tag, { color: PAYMENT_STATE_COLORS[invoice.paymentState], children: PAYMENT_STATE_LABELS[invoice.paymentState] })) : (_jsx(Text, { type: "secondary", children: "\u2014" }));
const handleExportRegistry = async (invoice, lines) => {
    await exportExcel(`${invoice.number}-registry.xlsx`, [
        excelSheet("Счёт", [
            { header: "Номер", value: (row) => row.number, type: "string" },
            { header: "Период с", value: (row) => dayjs(row.periodStart).toDate(), type: "date" },
            { header: "Период по", value: (row) => dayjs(row.periodEnd).toDate(), type: "date" },
            { header: "Выставлен", value: (row) => dayjs(row.issueDate).toDate(), type: "date" },
            { header: "Срок", value: (row) => dayjs(row.dueDate).toDate(), type: "date" },
            { header: "Итог", value: (row) => row.total, type: "number" },
            { header: "Состояние", value: (row) => (row.status === "VOID" ? INVOICE_VOID_LABEL : row.paymentState ? PAYMENT_STATE_LABELS[row.paymentState] : "—"), type: "string" },
        ], [invoice]),
        excelSheet("Строки", [
            { header: "Дата", value: (row) => dayjs(row.lineDate).toDate(), type: "date" },
            { header: "Заказ", value: (row) => row.orderNumber ?? "—", type: "string" },
            { header: "Описание", value: (row) => row.description ?? "—", type: "string" },
            { header: "Сотрудник", value: (row) => row.employeeLabel ?? "—", type: "string" },
            { header: "кВт⋅ч", value: (row) => row.kwh, type: "number" },
            { header: "Цена", value: (row) => row.pricePerKwh, type: "number" },
            { header: "Сбор", value: (row) => row.serviceFee, type: "number" },
            { header: "Сумма", value: (row) => row.amount, type: "number" },
        ], lines),
    ]);
};
/** Строки счёта (UC1120 §2.5, `v_corp_invoice_lines`) — «Выгрузить реестр» выдаёт `.xlsx`. */
const InvoiceLinesDrawer = observer(({ invoice, lines, onClose }) => {
    const columns = [
        {
            title: "Дата",
            key: "lineDate",
            render: (_, line) => formatDate(line.lineDate),
        },
        {
            title: "Заказ",
            key: "orderNumber",
            render: (_, line) => line.orderNumber ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
        },
        {
            title: "Описание",
            key: "description",
            render: (_, line) => line.description ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
        },
        {
            title: "Сотрудник",
            key: "employeeLabel",
            render: (_, line) => line.employeeLabel ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
        },
        {
            title: "кВт⋅ч",
            key: "kwh",
            render: (_, line) => line.kwh ?? _jsx(Text, { type: "secondary", children: "\u2014" }),
        },
        {
            title: "Цена",
            key: "pricePerKwh",
            render: (_, line) => (line.pricePerKwh === null ? _jsx(Text, { type: "secondary", children: "\u2014" }) : formatMoney(line.pricePerKwh)),
        },
        {
            title: "Сбор",
            key: "serviceFee",
            render: (_, line) => (line.serviceFee === null ? _jsx(Text, { type: "secondary", children: "\u2014" }) : formatMoney(line.serviceFee)),
        },
        {
            title: "Сумма",
            key: "amount",
            render: (_, line) => formatMoney(line.amount),
        },
    ];
    return (_jsx(Drawer, { title: invoice ? `Строки счёта ${invoice.number}` : "Строки счёта", open: Boolean(invoice), onClose: onClose, width: 900, children: invoice ? (_jsxs(_Fragment, { children: [_jsxs(Descriptions, { size: "small", column: 2, className: styles.drawer__summary, children: [_jsx(Descriptions.Item, { label: "\u041D\u043E\u043C\u0435\u0440", children: invoice.number }), _jsx(Descriptions.Item, { label: "\u0421\u043E\u0441\u0442\u043E\u044F\u043D\u0438\u0435", children: invoiceStatusTag(invoice) }), _jsxs(Descriptions.Item, { label: "\u041F\u0435\u0440\u0438\u043E\u0434", children: [formatDate(invoice.periodStart), " \u2013 ", formatDate(invoice.periodEnd)] }), _jsxs(Descriptions.Item, { label: "\u0418\u0442\u043E\u0433", children: [formatMoney(invoice.total), " \u20BD"] })] }), _jsx("div", { className: styles.drawer__toolbar, children: _jsx(Button, { icon: _jsx(DownloadOutlined, {}), "data-testid": "corp-report-registry", disabled: !lines, onClick: () => lines && void handleExportRegistry(invoice, lines), children: "\u0412\u044B\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0440\u0435\u0435\u0441\u0442\u0440" }) }), lines ? (_jsx(Table, { rowKey: "lineId", size: "middle", columns: columns, dataSource: lines, pagination: lines.length > 20 ? { pageSize: 20 } : false, locale: { emptyText: "Строк пока нет" } })) : (_jsx(Skeleton, { active: true, paragraph: { rows: 4 } }))] })) : null }));
});
InvoiceLinesDrawer.displayName = "InvoiceLinesDrawer";
export default InvoiceLinesDrawer;
